import { Suspense, lazy } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { SUPPORTED_LANGUAGES } from "../core/i18n";
import useLanguageStore from "../stores/languageStore";
import { getLocalizedPath } from "../utils/urlUtils";

// Lazy load the components - they export as named "Component"
const HomeComponent = lazy(() =>
  import("../routes/home").then((module) => ({ default: module.Component })),
);
const DiscoverComponent = lazy(() =>
  import("../routes/discover").then((module) => ({
    default: module.Component,
  })),
);
const CharacterInfoComponent = lazy(() =>
  import("../routes/character-info").then((module) => ({
    default: module.default,
  })),
);
const CreatorCenterComponent = lazy(() =>
  import("../routes/creator-center").then((module) => ({
    default: module.Component,
  })),
);
const UserPersonaComponent = lazy(() =>
  import("../routes/my-persona").then((module) => ({
    default: module.Component,
  })),
);
const CreateCharacterComponent = lazy(() =>
  import("../routes/create").then((module) => ({
    default: module.Component,
  })),
);
const CreateImageComponent = lazy(() =>
  import("../routes/create-images").then((module) => ({
    default: module.Component,
  })),
);
const MyImagesComponent = lazy(() =>
  import("../routes/my-images").then((module) => ({
    default: module.Component,
  })),
);
const CreatorCompetitionComponent = lazy(() =>
  import("../routes/creator-competition").then((module) => ({
    default: module.Component,
  })),
);
const DownloadAppsComponent = lazy(() =>
  import("../routes/download-apps").then((module) => ({
    default: module.Component,
  })),
);

export function LanguageRedirect(): JSX.Element {
  const location = useLocation();
  // Use getState() to get the current value synchronously
  // This ensures we always read the latest value from the store, even if the component
  // hasn't re-rendered yet after setLanguage is called. The component will re-render
  // when the location changes (via useLocation), at which point it will read the updated language.
  const currentLanguage = useLanguageStore.getState().currentLanguage;

  // Ensure we have a valid language
  const validLanguage =
    currentLanguage in SUPPORTED_LANGUAGES ? currentLanguage : "en";

  // If path starts with /en/, redirect to remove it (English doesn't use prefix)
  if (location.pathname.startsWith("/en/") || location.pathname === "/en") {
    const pathWithoutEn = location.pathname.replace(/^\/en/, "") || "/";
    const newPath = pathWithoutEn + location.search;
    return <Navigate to={newPath} replace />;
  }

  // Calculate the correct path for the current language
  const correctPath = getLocalizedPath(location.pathname, validLanguage);
  const newPath = correctPath + location.search;

  // If we're already on the correct path, don't redirect
  if (location.pathname + location.search === newPath) {
    // For English, render components directly for supported routes
    if (validLanguage === "en") {
      if (location.pathname === "/") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <HomeComponent />
          </Suspense>
        );
      } else if (location.pathname === "/discover") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <DiscoverComponent />
          </Suspense>
        );
      } else if (location.pathname.startsWith("/tag/")) {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <DiscoverComponent />
          </Suspense>
        );
      } else if (location.pathname.startsWith("/character-info/")) {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <CharacterInfoComponent />
          </Suspense>
        );
      } else if (location.pathname === "/creator-center") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <CreatorCenterComponent />
          </Suspense>
        );
      } else if (location.pathname === "/my-images") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <MyImagesComponent />
          </Suspense>
        );
      } else if (location.pathname === "/user-persona") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <UserPersonaComponent />
          </Suspense>
        );
      } else if (location.pathname === "/create-character") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <CreateCharacterComponent />
          </Suspense>
        );
      } else if (location.pathname === "/create-image") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <CreateImageComponent />
          </Suspense>
        );
      } else if (location.pathname === "/creator-competition") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <CreatorCompetitionComponent />
          </Suspense>
        );
      } else if (location.pathname === "/download/apps") {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <DownloadAppsComponent />
          </Suspense>
        );
      }
    }

    // For other paths that are already correct, don't redirect
    // Return a fragment to satisfy JSX.Element type
    return <></>;
  }

  return <Navigate to={newPath} replace />;
}
